import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTransitionStyles,
  useTypeahead,
} from '@floating-ui/react'
import {type OptionValue, cn} from '@tempmee/core'
import {OutlineChevronDownIcon} from '@tempmee/react-icons'
import {
  type KeyboardEvent,
  type ReactNode,
  forwardRef,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import {Center} from '../box'
import {CircularProgress} from '../circular-progress'
import {FieldHelperText} from '../input-text-field/FieldLabel'
import {MenuItemVariantsFactory} from '../menu/Variants'

export interface InputSelectFieldProps<T = string> {
  /** List of selectable options */
  options: OptionValue<T>[]
  /** Currently selected value */
  value?: T
  /** Callback when an option is selected */
  onChange?: (value: T) => void
  /** Callback fired when the dropdown closes */
  onClose?: () => void
  /**
   * Floating label rendered inside the field; doubles as the placeholder when
   * empty (MUI filled style). When omitted, `placeholder` text is shown instead.
   */
  label?: string
  /** Placeholder text shown when no value is selected and no `label` is set */
  placeholder?: string
  /**
   * Supporting text rendered below the field, carrying a hint or a validation
   * message. Takes the `error` color when the field is in error. Matches the
   * native twin, which has carried `helperText` since it shipped.
   */
  helperText?: ReactNode
  /** Icon rendered on the left side of the trigger */
  leadingIcon?: ReactNode
  /** Applies error styling (pink fill + red underline). Mirrors MUI's `Select`/`TextField` `error`. */
  error?: boolean
  /**
   * @deprecated Use `error` instead (renamed in v3 to match MUI + `TextField`).
   * Still honored for back-compat.
   */
  isError?: boolean
  /** Disables the trigger */
  disabled?: boolean
  /** Shows a spinner inside the dropdown while options are loading */
  isLoading?: boolean
  /** Additional className for the trigger container */
  className?: string
}

function InputSelectFieldInner<T = string>(
  {
    className,
    options,
    value,
    onChange,
    onClose,
    label,
    helperText,
    placeholder = 'Select...',
    leadingIcon,
    error,
    isError = false,
    disabled = false,
    isLoading = false,
  }: InputSelectFieldProps<T>,
  ref: React.Ref<HTMLButtonElement>,
) {
  // `error` is the v3 (MUI-aligned) name; `isError` is the deprecated alias.
  const hasError = error ?? isError
  const helperTextId = useId()
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const listRef = useRef<(HTMLElement | null)[]>([])

  const selectedIndex = useMemo(() => options.findIndex((o) => o.value === value), [options, value])
  const selectedOption = selectedIndex === -1 ? undefined : options[selectedIndex]

  // Typeahead matches against the option labels, so `useTypeahead` needs a
  // parallel ref of strings alongside `listRef`'s DOM nodes.
  const labels = useMemo(() => options.map((o) => o.label), [options])
  const listContentRef = useRef(labels)
  listContentRef.current = labels
  // Set while a typeahead string is being composed, so a space typed mid-word
  // ("front desk") searches instead of selecting the highlighted row.
  const isTypingRef = useRef(false)

  // Filled-field floating label: floats up when open or a value is selected.
  const shrink = isOpen || selectedOption != null

  const {refs, floatingStyles, context} = useFloating<HTMLButtonElement>({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open)
      if (!open) onClose?.()
    },
    placement: 'bottom-start',
    // The listbox is portalled to <body>, so without autoUpdate its position is
    // computed once on open and stays put while a scrolling ancestor (or a
    // resize) moves the trigger.
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(0),
      flip({padding: 8}),
      size({
        apply({rects, elements}) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          })
        },
        padding: 8,
      }),
    ],
  })

  const click = useClick(context, {enabled: !disabled})
  const dismiss = useDismiss(context)
  const role = useRole(context, {role: 'listbox'})
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex: selectedIndex === -1 ? null : selectedIndex,
    onNavigate: setActiveIndex,
    enabled: !isLoading,
  })
  const typeahead = useTypeahead(context, {
    listRef: listContentRef,
    activeIndex,
    selectedIndex: selectedIndex === -1 ? null : selectedIndex,
    enabled: !disabled && !isLoading,
    onTypingChange: (isTyping) => {
      isTypingRef.current = isTyping
    },
    onMatch: (index) => {
      if (isOpen) {
        setActiveIndex(index)
        return
      }
      // Closed trigger: typing commits the match without opening the list,
      // the way a native <select> does.
      const option = options[index]
      if (option && option.value !== value) onChange?.(option.value)
    },
  })

  const {getReferenceProps, getFloatingProps, getItemProps} = useInteractions([
    click,
    dismiss,
    role,
    listNav,
    typeahead,
  ])

  const {isMounted, styles: transitionStyles} = useTransitionStyles(context, {duration: 150})

  const handleSelect = (optionValue: T) => {
    onChange?.(optionValue)
    setIsOpen(false)
    onClose?.()
  }

  const handleKeyDown = (e: KeyboardEvent, optionValue: T) => {
    if (e.key === 'Enter' || (e.key === ' ' && !isTypingRef.current)) {
      e.preventDefault()
      handleSelect(optionValue)
    }
  }

  const labelColor = disabled
    ? 'text-text-disabled'
    : shrink
      ? 'text-text-primary'
      : 'text-text-secondary'

  return (
    <>
      <button
        ref={(node) => {
          refs.setReference(node)
          if (typeof ref === 'function') ref(node)
          else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node
        }}
        type="button"
        disabled={disabled}
        aria-expanded={isOpen}
        aria-describedby={helperText == null ? undefined : helperTextId}
        className={cn(
          'relative flex w-full items-center gap-2 rounded-t-[4px] border-b border-solid px-3 py-2 text-left transition-colors',
          hasError
            ? 'border-error bg-[rgba(255,128,140,0.16)]'
            : 'bg-[#f6f9f8] border-divider focus-visible:border-primary',
          isOpen && !hasError && 'border-primary',
          disabled && 'cursor-not-allowed',
          'focus-visible:outline-none',
          className,
        )}
        {...getReferenceProps()}
      >
        {leadingIcon && (
          <span
            className={cn(
              'flex shrink-0 items-center justify-center size-6 text-text-secondary [&_svg]:size-6',
              disabled && 'text-text-disabled',
            )}
          >
            {leadingIcon}
          </span>
        )}

        <span className="relative flex h-10 flex-1 items-center">
          {label && (
            <span
              className={cn(
                'pointer-events-none absolute left-0 origin-left tracking-[0.15px] transition-all duration-150 ease-out',
                shrink
                  ? 'top-0 text-[12px] font-normal leading-4'
                  : '-translate-y-1/2 top-1/2 text-base font-semibold leading-6',
                labelColor,
              )}
            >
              {label}
            </span>
          )}
          <span
            className={cn(
              'block w-full truncate text-base font-semibold leading-6',
              label && 'absolute inset-0 h-full pt-4',
              disabled
                ? 'text-text-disabled'
                : selectedOption
                  ? 'text-text-primary'
                  : 'text-text-disabled',
            )}
          >
            {selectedOption?.label ?? (label ? '' : placeholder)}
          </span>
        </span>

        <OutlineChevronDownIcon
          className={cn(
            'size-6 shrink-0 ml-1 transition-transform duration-150',
            disabled ? 'text-text-disabled' : 'text-text-secondary',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {helperText != null && (
        <FieldHelperText id={helperTextId} disabled={disabled} isError={hasError}>
          {helperText}
        </FieldHelperText>
      )}

      {isMounted && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            {/* The listbox portals to <body>, so it must out-rank the overlay
                tiers to stay usable when the field lives inside one (MUI scale:
                drawer 1200 < modal 1300 < snackbar 1400). `z-50` rendered the
                open dropdown BEHIND a Dialog. `z-[1500]` (MUI's tooltip tier)
                keeps an open, focused list above every overlay. */}
            <div
              ref={refs.setFloating}
              style={{...floatingStyles, ...transitionStyles}}
              className="ds-scroll-area z-[1500] max-h-60 overflow-y-auto rounded-b-lg bg-background p-1 shadow-elevation-1 outline-none"
              {...getFloatingProps()}
            >
              {isLoading ? (
                <Center py={4}>
                  <CircularProgress size={24} />
                </Center>
              ) : (
                options.map((option, index) => {
                  const isSelected = option.value === value
                  return (
                    <div
                      key={String(option.value)}
                      ref={(node) => {
                        listRef.current[index] = node
                      }}
                      // biome-ignore lint/a11y/useSemanticElements: role="option" is required here; native <option> is only valid inside <select>/<datalist>
                      role="option"
                      aria-selected={isSelected}
                      tabIndex={activeIndex === index ? 0 : -1}
                      // Reuses the DS MenuItem visual language (the `_MenuItem` atom the Figma
                      // references): plain text rows, light `action.hover`, teal-pill `selected`.
                      className={cn(
                        MenuItemVariantsFactory({selected: isSelected}),
                        'select-none',
                        activeIndex === index && !isSelected && 'bg-action-hover',
                      )}
                      onKeyDown={(e) => handleKeyDown(e, option.value)}
                      {...getItemProps({
                        onClick: () => handleSelect(option.value),
                      })}
                    >
                      <span className="min-w-0 flex-1 truncate text-left">{option.label}</span>
                    </div>
                  )
                })
              )}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  )
}

export const InputSelectField = forwardRef(InputSelectFieldInner) as <T = string>(
  props: InputSelectFieldProps<T> & {ref?: React.Ref<HTMLButtonElement>},
) => React.ReactElement
